<template>
  <main id="mainContent">
    <Stepper :step="stepNum" />
    <NotificationStatus :show-message="displayNotification" />
    <div class="app-main-content">
      <h1>{{ screenData.title }}</h1>
      <div
        v-html="screenData.descriptionwithtoken"
        v-if="store.state.user.onlineToken != '' && store.state.user.onlineToken != null"
      ></div>
      <div v-html="screenData.description" v-else></div>

      <div class="app-content-panels">
        <div class="app-panel-left">
          <BaseButtonGroup>
            <button type="button" @click="handleRouting('info')">
              {{ language.btnReNotify }}
            </button>
            <button type="button" @click="handleRouting('cal')">
              {{ language.btnAddCal }}
            </button>
            <button
              type="button"
              class="app-btn-primary"
              @click="handleRouting('print')"
            >
              {{ hasReceipt ? language.btnPrintResReceipt : language.btnPrintRes }}
            </button>
          </BaseButtonGroup>

          <ServiceInfo :show-barcode="true" />

          <PaymentReceipt v-if="hasReceipt" />

          <BaseButtonGroup>
            <button
              class="button-home"
              type="button"
              @click="handleRouting('close')"
              v-if="store.state.user.onlineToken != '' && store.state.user.onlineToken != null"
            >
              {{ language.btnClose }}
            </button>
            <button
              class="button-home"
              type="button"
              @click="handleRouting('home')"
              v-else
            >
              {{ language.btnResHome }}
            </button>
          </BaseButtonGroup>
        </div>
        <AsideForLinks />
      </div>

      <ModalContact
        :show-modal="showContactForm"
        @doClose="showContactForm = false"
        @doNotificationMessage="handleModalNotification"
      />
    </div>
  </main>
</template>


<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter, onBeforeRouteLeave } from 'vue-router';
import { parse, add } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import appConfig from '@/app.config';
import Stepper from '@/components/reservation/Stepper';
import NotificationStatus from '@/components/reservation/confirmation/NotificationStatus';
import ServiceInfo from '@/components/reservation/confirmation/ServiceInfo';
import PaymentReceipt from '@/components/reservation/confirmation/PaymentReceipt';
import AsideForLinks from '@/components/reservation/aside/AsideForLinks';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import ModalContact from '@/components/reservation/confirmation/ModalContact';
import { useHead } from '@unhead/vue';

useHead({
  page: {
    title: `Reservation finalized | ${appConfig.shortName}`,
    meta: [
      {
        name: 'description',
        content: 'Print or save your confirmation number for check-in.'
      }
    ]
  }
});
// Vuex route and store
const store = useStore();
const router = useRouter();

// Reactive State
const stepNum = 5;
const showContactForm = ref(false);
const modalCaller = 'getContact';
const displayNotification = ref(true);
const fromRoute = ref(undefined);
const screenData = ref({})

// Computed properties
const language = computed(() => store.state.user.LANG);
const hasReceipt = computed(() => store.state.user.resFeeApplicable && store.state.user.resDetails.userInfo && store.state.user.resDetails.userInfo.paymentReceipt);
const resAppointmentLocation = computed(() => store.state.user.resAppointmentLocation);
const resDetails = computed(() => store.state.user.resDetails);
const resAppointmentNum = computed(() => store.state.user.resAppointmentNum);
const PAGES = computed(() => store.state.user.PAGES);

// Lifecycle hooks
onMounted(() => {
  // Check if navigating from 'Confirmation' or 'Payment' to display notification
  if (fromRoute.value) {
    displayNotification.value = fromRoute.value.name === 'Confirmation' || fromRoute.value.name === 'Payment';
  }
});

onUnmounted(() => {
  // Clean up all reservation details regardless of how this component is destroyed
  store.dispatch('user/resetReservationDetails', false);
});

// Navigation guard (composition API equivalent for beforeRouteEnter logic)
onBeforeRouteLeave((to, from) => {
  fromRoute.value = from;
});

// Watchers
watch(showContactForm, (newVal) => {
  if (!newVal) {
    nextTick(() => {
      const caller = document.getElementById(modalCaller);
      if (caller) {
        caller.focus();
      }
    });
  }
});

// Component setup
if (resAppointmentNum.value.length > 0) {
  // Logic from created hook, now runs immediately in setup
  // The screenData computed property handles the dynamic content.
  screenData.value = store.state.user.STEPS[stepNum];
} else {
  // Redirect if reservation number is missing
  router.push({ name: 'Confirmation' });
}

// Methods
const handleModalNotification = (event) => {
  store.dispatch('user/setStoreProperty', { property: 'resNotificationSent', value: event });
  displayNotification.value = true;
};

const handleRouting = (target) => {
  switch (target) {
    case 'info':
      getContactInfo();
      break;
    case 'cal':
      createCalendarFile();
      break;
    case 'print':
      printConfirmation();
      break;
    case 'close':
      closeWithToken();
      break;
    case 'home':
      homeConfirmationPage();
      break;
  }
};

const homeConfirmationPage = () => {
  router.push({ name: PAGES.value.home });
};

const closeWithToken = () => {
  localStorage.removeItem('onlineToken');
  localStorage.removeItem('confirmationNumber');
  setTimeout(() => {
    window.close();
  }, 200);
};

const createCalendarFile = () => {
  const reservationLocation = resAppointmentLocation.value;
  let address = reservationLocation.addressLine1;
  address = reservationLocation.addressLine2?.length > 0 ? `${address}, ${reservationLocation.addressLine2}` : address;
  address = `${(address).trim()}, ${reservationLocation.city}, ${reservationLocation.state} ${reservationLocation.zip}`;
  address = reservationLocation.zipPlus?.length > 0 ? `${address}-${reservationLocation.zipPlus}` : address;

  const startDate = resDetails.value.serviceDate.replace('T00:00:00', '');
  const description = `Service(s):\\n${resDetails.value.serviceType}\\n\\nLocation:\\n${address}\\n\\nConfirmation number:\\n${resAppointmentNum.value}\\n\\nIf you are unable to keep this reservation, please be courteous and cancel it by clicking https://ridmvreservations.ri.gov/lookup so that the reservation can be made available to another customer. To modify the date/time of your reservation or your reservation details, click https://ridmvreservations.ri.gov/lookup`;
  const startDateTime = parse(`${startDate} ${resDetails.value.internalServiceTime}`, 'yyyy-MM-dd HH:mm:ss', new Date());
  const utcDateTime = zonedTimeToUtc(startDateTime, 'America/New_York');
  const startFormatted = utcDateTime.toISOString().split('-').join('').split(':').join('').replace('.000Z', 'Z');
  const endFormatted = add(utcDateTime, { hours: 1 }).toISOString().split('-').join('').split(':').join('').replace('.000Z', 'Z');

  const url = 'BEGIN:VCALENDAR\n' +
    'VERSION:2.0\n' +
    'BEGIN:VEVENT\n' +
    `DTSTART:${startFormatted}\n` +
    `DTEND:${endFormatted}\n` +
    'SUMMARY:RI DMV Reservation Confirmation\n' +
    `DESCRIPTION:${description}\n` +
    `LOCATION:${resDetails.value.serviceLocation.location}\n` +
    'BEGIN:VALARM\n' +
    'TRIGGER:-P1D\n' +
    'ACTION:DISPLAY\n' +
    'DESCRIPTION:RI DMV Reservation Confirmation\n' +
    'END:VALARM\n' +
    'END:VEVENT\n' +
    'END:VCALENDAR';

  const link = document.createElement('a');
  link.href = `data:text/calendar;charset=utf8,${encodeURIComponent(url)}`;
  link.setAttribute('download', `reservation_${resAppointmentNum.value.toLowerCase()}.ics`);
  document.body.appendChild(link);
  link.click();
};

const getContactInfo = () => {
  displayNotification.value = false;
  showContactForm.value = false;
  nextTick(() => {
    showContactForm.value = true;
  });
};

const printConfirmation = () => {
  window.print();
};
</script>


<style lang="scss" scoped>
  .app-panel-left::v-deep .button-group {
    padding-bottom: calc(var(--padding-vertical) * .75);
  }
  .button-home {
    width: max-content;
    margin-bottom: calc(var(--padding-vertical) * 2);
    @media print {
      display: none;
    }
  }

  .app-button-group {
    button {
      margin-bottom: 1.125em;
      &:not(:last-child) {
        margin-right: 1em;
      }
      &.disabled {
        cursor: not-allowed;
        opacity: 0.7;
      }
    }
  }
</style>
